import { Controller } from "@hotwired/stimulus";

// Drives the RCS rich-message editor on the SMS template form. The visible card
// inputs are nameless widgets; this controller serializes them into the hidden
// rcs_content JSON field (mirroring how the Draft.js body editor submits JSON)
// and keeps the live card preview in sync.
export default class extends Controller {
  static targets = [
    "typeSelect",
    "contentInput",
    "cardEditor",
    "titleInput",
    "titleCounter",
    "subtitleInput",
    "subtitleCounter",
    "mediaUrlInput",
    "imageFileInput",
    "actionsList",
    "actionRowTemplate",
    "addActionButton",
    "previewImage",
    "previewTitle",
    "previewSubtitle",
    "previewActions",
  ];

  static values = {
    maxActions: Number,
    actionTitleMaxLengths: Object,
    attachedMediaUrl: String,
  };

  connect() {
    this.typeChanged();
  }

  typeChanged() {
    this.cardEditorTarget.classList.toggle("hidden", !this.cardSelected());
    this.serialize();
  }

  addAction() {
    if (this.actionRows().length >= this.maxActionsValue) return;

    this.actionsListTarget.appendChild(
      this.actionRowTemplateTarget.content.cloneNode(true)
    );
    this.serialize();
  }

  removeAction(event) {
    event.target.closest("[data-rcs-action-row]").remove();
    this.serialize();
  }

  serialize() {
    this.updateCounters();
    this.actionRows().forEach((row) => {
      this.syncRowTitleLimit(row);
      this.syncRowValueField(row);
    });
    this.addActionButtonTarget.classList.toggle(
      "hidden",
      this.actionRows().length >= this.maxActionsValue
    );
    this.contentInputTarget.value = JSON.stringify(
      this.cardSelected() ? this.cardContent() : {}
    );
    this.updatePreview();
  }

  // The inputs' maxlength blocks typing past the limit; the counters make the
  // remaining room visible and turn amber at the cap.
  updateCounters() {
    this.updateCounter(this.titleInputTarget, this.titleCounterTarget);
    this.updateCounter(this.subtitleInputTarget, this.subtitleCounterTarget);
  }

  updateCounter(input, counter) {
    const length = input.value.length;
    const max = input.maxLength;
    counter.textContent = `${length}/${max}`;
    counter.classList.toggle("text-amber-600", length >= max);
    counter.classList.toggle("text-gray-500", length < max);
  }

  imageFileChosen() {
    const file = this.imageFileInputTarget.files[0];
    this.pendingImageUrl = file ? URL.createObjectURL(file) : null;
    this.serialize();
  }

  previewImageFailed() {
    this.previewImageTarget.classList.add("hidden");
  }

  previewImageLoaded() {
    this.previewImageTarget.classList.remove("hidden");
  }

  cardSelected() {
    return this.typeSelectTarget.value === "twilio/card";
  }

  cardContent() {
    const content = { title: this.titleInputTarget.value.trim() };

    const subtitle = this.subtitleInputTarget.value.trim();
    if (subtitle) content.subtitle = subtitle;

    const mediaUrl = this.mediaUrlInputTarget.value.trim();
    if (mediaUrl) content.media = [mediaUrl];

    const actions = this.actionRows()
      .map((row) => this.rowContent(row))
      .filter(Boolean);
    if (actions.length) content.actions = actions;

    return content;
  }

  rowContent(row) {
    const type = row.querySelector("[data-rcs-role=action-type]").value;
    const title = row.querySelector("[data-rcs-role=action-title]").value.trim();
    const value = row.querySelector("[data-rcs-role=action-value]").value.trim();
    if (!title && !value) return null;

    const action = { type, title };
    if (type === "URL") action.url = value;
    if (type === "PHONE_NUMBER") action.phone = value;
    if (type === "QUICK_REPLY" && value) action.id = value;
    return action;
  }

  // The value input means something different per action type; keep its
  // placeholder honest and hide it for quick replies, where it is optional.
  // Twilio caps quick-reply labels shorter than URL/phone labels, so each row's maxlength
  // follows its selected type. Text already typed past a lower limit isn't truncated here;
  // the server validation reports it on save.
  syncRowTitleLimit(row) {
    const type = row.querySelector("[data-rcs-role=action-type]").value;
    const limit = this.actionTitleMaxLengthsValue[type];
    if (limit) row.querySelector("[data-rcs-role=action-title]").maxLength = limit;
  }

  syncRowValueField(row) {
    const type = row.querySelector("[data-rcs-role=action-type]").value;
    const valueInput = row.querySelector("[data-rcs-role=action-value]");
    const placeholders = {
      URL: "https://…",
      PHONE_NUMBER: "+15551234567",
      QUICK_REPLY: "Reply payload (optional)",
    };
    valueInput.placeholder = placeholders[type];
  }

  actionRows() {
    return Array.from(
      this.actionsListTarget.querySelectorAll("[data-rcs-action-row]")
    );
  }

  updatePreview() {
    const content = this.cardSelected() ? this.cardContent() : {};

    this.previewTitleTarget.textContent = content.title || "Card title";
    this.previewSubtitleTarget.textContent = content.subtitle || "";
    this.previewSubtitleTarget.classList.toggle("hidden", !content.subtitle);

    const previewableUrl = this.previewMediaUrl();
    if (previewableUrl && previewableUrl !== this.previewImageTarget.src) {
      this.previewImageTarget.src = previewableUrl;
    } else if (!previewableUrl) {
      this.previewImageTarget.removeAttribute("src");
      this.previewImageTarget.classList.add("hidden");
    }

    this.previewActionsTarget.replaceChildren(
      ...(content.actions || []).map((action) => this.previewActionElement(action))
    );
  }

  // Mirrors the server-side precedence: a freshly chosen file, then the already-uploaded
  // attachment, then the external URL — which only previews once it's a complete http(s)
  // URL, since a partial value like "ht" would be requested relative to the page and 404.
  previewMediaUrl() {
    if (this.pendingImageUrl) return this.pendingImageUrl;
    if (this.attachedMediaUrlValue) return this.attachedMediaUrlValue;

    const url = this.mediaUrlInputTarget.value.trim();
    return /^https?:\/\/\S+\.\S+/.test(url) ? url : null;
  }

  previewActionElement(action) {
    const element = document.createElement("div");
    element.className =
      "px-3 py-2 text-center text-sm font-medium text-blue-600 border-b border-gray-100 last:border-b-0";
    element.textContent = action.title || "Button";
    return element;
  }
}
